import { importWithRetry } from '@trello/use-lazy-component';
import { slackTrelloDomain } from '@trello/config';
import { trelloFetch } from '@trello/fetch';
import { token } from '@trello/session-cookie';
import { parseNetworkError } from '@trello/graphql-error-handling';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { QueryTrelloSlackAssociationArgs } from '../generated';
import {
  prepareDataForApolloCache,
  QueryInfo,
} from '../prepareDataForApolloCache';
import { ResolverContext } from '../types';

export const getVisitorFingerprint = async () => {
  // Get the visitor identifier when you need it.
  const { default: FingerprintJS } = await importWithRetry(
    () =>
      import(
        /* webpackChunkName: "fingerprint-js" */ '@fingerprintjs/fingerprintjs'
      ),
  );
  const fpPromise = FingerprintJS.load();
  const fp = await fpPromise;
  const result = await fp.get();
  // Remove components that are messing up the fingerprint in Safari and Brave
  const {
    audio,
    canvas,
    cookiesEnabled,
    deviceMemory,
    domBlockers,
    hardwareConcurrency,
    plugins,
    vendorFlavors,
    ...components
  } = result.components;

  // This is the visitor identifier:
  const visitorFingerprint = FingerprintJS.hashComponents({ ...components });
  return visitorFingerprint;
};

export const trelloSlackAssociationResolver = async (
  obj: object,
  { workspaceId }: QueryTrelloSlackAssociationArgs,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const fp = await getVisitorFingerprint();
  const queryParams = new URLSearchParams({ fp });
  const trelloSlackServiceUrl = `${slackTrelloDomain}/trello/${workspaceId}/association?${queryParams}`;

  const response = await trelloFetch(
    trelloSlackServiceUrl,
    {
      credentials: 'same-origin',
      headers: {
        authorization: `token=${token}`,
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
    },
    {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'trelloSlackAssociation',
        operationType: 'query',
        operationName: context.operationName,
      },
    },
  );

  if (response.ok) {
    const json = await response.json();
    return prepareDataForApolloCache(json, info);
  }

  sendNetworkErrorEvent({
    url: trelloSlackServiceUrl,
    response: await response.clone().text(),
    status: response.status,
    operationName: context.operationName,
  });
  throw await parseNetworkError(response);
};
